import { process } from '@progress/kendo-data-query';
import { formatNumber } from '@progress/kendo-intl';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridItemChangeEvent,
  GridPagerSettings,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { toNumber } from 'lodash';
import moment from 'moment-timezone';
import { FC, useCallback, useMemo, useState } from 'react';
import useCheckbookYearColumns from 'src/assets/static/view_checkbook/useCheckbookYearColumns';
import GridWrapper, { GridColumn } from 'src/components/GridWrapper';
import Button from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import useBoolean from 'src/hooks/useBoolean';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import useWindowSize from 'src/hooks/useWindowSize';
import useYearView from 'src/hooks/useYearView';
import { GLCodePeriodDepartment } from 'src/services/useGlCodeDepartmentApi';
import { GLCodesBudgetByPeriodBudgets } from 'src/services/useGlCodesBudgetByYearApi';
import { IFilters } from 'src/types/PostBody';
import exportToExcel from 'src/utils/exportToExcel';
import { modifyFilters } from 'src/utils/requestInterceptor';
import { sliceFiveChar } from 'src/utils/strings';
import { GLPeriodYearDropdown } from 'src/utils/transformation';
import DepartmentsTable from './DepartmentsTable';
import ViewCheckbookActions from './ViewCheckbookActions';
import ViewCheckbookYearTableToolbar from './ViewCheckbookYearTableToolbar';
import { isColumnVisible } from './ViewPeriodCheckbookTable';
import YearFilterPills from './YearFilterPills';

const selectable: GridSelectableSettings = {
  enabled: false,
  drag: false,
  cell: true,
};

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100, 5000],
};

const defaultProps = {
  excelFileName: 'View Checkbook.xlsx',
  tableTextId: 'GL Codes',
  DataItemKey: 'glCode',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

export const YEAR_VIEW_DATE_FIELD_ARRAY = ['statusDate', 'poDate'];
export const YEAR_VIEW_YEAR_FIELD_ARRAY = [
  'poNum',
  'invoiceNumber',
  'status',
  'supplierName',
  'poStatus',
  'hasGlMismatchItems',
  'poItemsCompleted',
  'poSubject',
  'buyerName',
];

interface ViewPeriodCheckbookTableProps {}

const ViewYearCheckbookTable: FC<ViewPeriodCheckbookTableProps> = () => {
  const {
    glCodesBudgetByYear,
    setGlCodesBudgetByYear,
    yearCheckbookDataState,
    setYearCheckbookDataState,
    isGlCodesBudgetByYearLoading,
    fiscalYear,
    dropDownYearValue,
    isEditMode,
    selectedCompany,
    selectedYearDepartment,
    t,
    selectedView,
    confirmBeforeProceeding,
    editingBudgetYearDataWithErrors,
    setEditingGlCodeYearBudget,
    buId,
    hasUsesInitialBudgetsPermission,
  } = useViewCheckbook();

  const {
    setYearViewDropdownFilter,
    yearViewDropdownFilter,
    getYearFilters,
    getYearSorts,
    yearViewData,
    yearViewTotal,
    editableColumnDropdownData,
  } = useYearView();

  const {
    value: openDepartmentsModal,
    setFalse: handleCloseDepartmentsModal,
    setTrue: handleOpenDepartmentsModal,
  } = useBoolean();

  const periodChildColumnField: string[] = useMemo(
    () =>
      hasUsesInitialBudgetsPermission ? ['initialBudgetPeriod', 'budgetPeriod'] : ['budgetPeriod'],
    [hasUsesInitialBudgetsPermission]
  );

  const [departments, setDepartments] = useState<GLCodePeriodDepartment[]>([]);
  const { width, height } = useWindowSize();
  const [excelLoading, setExcelLoading] = useState<boolean>(false);

  const isColumnMenuDisable = useMemo(() => {
    const filters: IFilters[] = modifyFilters(yearCheckbookDataState.filter);
    const isDefaultFilter =
      filters.length <= 2 &&
      filters.every((item) => item.field === 'isDeleted' || item.field === 'isActive');
    return !fiscalYear
      ? true
      : fiscalYear && glCodesBudgetByYear?.length === 0 && isDefaultFilter
      ? true
      : false;
  }, [yearCheckbookDataState.filter, glCodesBudgetByYear?.length, fiscalYear]);

  const viewYearCheckbookColumns = useCheckbookYearColumns(
    isEditMode,
    isColumnMenuDisable,
    dropDownYearValue?.periods?.length ?? 12
  );

  const yearViewColumns = useMemo(() => {
    let columns: GridColumn<any>[] = viewYearCheckbookColumns.map((item) => {
      const periodColumn = item.field?.includes('period');
      const glCodeColumn = item.field?.includes('glcode_type');
      const departmentsColumn = item.field?.includes('general_ledger');
      if (periodColumn) {
        const hideParentColumn = periodChildColumnField.every(
          (field) => isColumnVisible(editableColumnDropdownData, field) === false
        );
        return {
          ...item,
          show: hideParentColumn ? false : true,
          childrenColumn: item.childrenColumn?.map((column) => ({
            ...column,
            show: isColumnVisible(
              editableColumnDropdownData,
              column.field?.slice(0, -2) || column.field || ''
            ),
          })),
        };
      }
      if (glCodeColumn || departmentsColumn) {
        const hideParentColumn = item?.childrenColumn?.every(
          (col) => isColumnVisible(editableColumnDropdownData, col.field as string) === false
        );
        return {
          ...item,
          show: hideParentColumn ? false : true,
          childrenColumn: item.childrenColumn?.map((column) => ({
            ...column,
            show: isColumnVisible(editableColumnDropdownData, column.field || ''),
          })),
        };
      }

      return {
        ...item,
        show: isColumnVisible(editableColumnDropdownData, item.field || ''),
      };
    });

    const lastStickyColumnIndex = columns.reduceRight((acc, obj, index) => {
      if (acc === -1 && obj.locked && obj.show) {
        return index;
      }
      return acc;
    }, -1);

    columns = columns.map((item, index) => {
      if (lastStickyColumnIndex === index) {
        return {
          ...item,
          headerClassName: `last-sticky-cell-divider ${item.headerClassName}`,
          className: `last-sticky-cell-divider ${item.className}`,
          childrenColumn: item.childrenColumn?.map((child, idx, arr) =>
            idx === arr.length - 1
              ? {
                  ...child,
                  headerClassName: `last-sticky-cell-divider ${child.headerClassName || ''}`,
                  className: `last-sticky-cell-divider ${child.className || ''}`,
                }
              : child
          ),
        };
      } else return item;
    });

    return columns;
  }, [editableColumnDropdownData, periodChildColumnField, viewYearCheckbookColumns]);

  const viewCheckbookExcelColumns = useMemo(
    () =>
      viewYearCheckbookColumns
        .map((col) =>
          col.field === 'general_ledger' || col.field === 'glcode_type' ? col.childrenColumn : col
        )
        .flat()
        .filter(
          (item) =>
            !['selected', 'hasErrorsOrWarnings', 'departments', 'AcrossCell'].includes(
              item?.field || ''
            )
        ),
    [viewYearCheckbookColumns]
  );
  const gridHeight: number = useMemo(() => height - 200, [height]);

  const excelExport = useCallback(
    (data: any[], isDepartmentIncluded: boolean) => {
      const columns: string[] = [];
      const columnMapping: Record<string, string> = {};
      const checkbookColumns = [
        ...(isDepartmentIncluded ? [{ field: 'department', title: t('Department') }] : []),
        { field: 'companyName', title: t('Company Name') },
        ...viewCheckbookExcelColumns,
      ];

      checkbookColumns.forEach((item) => {
        if (item && !item.field?.includes('period')) {
          columns.push(item.field as string);
          columnMapping[item.field as string] = item.title || '';
        }
      });
      const { periods = [], fiscalYear = 0 } = dropDownYearValue ?? ({} as GLPeriodYearDropdown);

      periods.forEach((period, index) => {
        const startDate = moment(new Date(period.startDate)).format('MM/DD');
        const endDate = moment(new Date(period.endDate)).format('MM/DD');
        const periodLabel = `${startDate} - ${endDate}`;
        const periodIndex = (index + 1).toString().padStart(2, '0');

        const initialField = `period${periodIndex} - Initial`;
        const forecastField = `period${periodIndex} - Forecast`;
        if (hasUsesInitialBudgetsPermission) {
          columns.push(initialField);
        }
        columns.push(forecastField);
        if (hasUsesInitialBudgetsPermission) {
          columnMapping[initialField] = `Period ${periodIndex}(${periodLabel}) - Budget`;
        }
        columnMapping[forecastField] = `Period ${periodIndex}(${periodLabel}) - Forecast`;
      });

      const rows = data.map((item) => {
        const forecastPeriods = (item.periodBudgets as GLCodesBudgetByPeriodBudgets[]).reduce(
          (result, periodItem, index) => {
            const key = `period${(index + 1).toString().padStart(2, '0')} - Forecast`;
            result[key] = formatNumber(periodItem.totalBudgetAmount, 'c2');
            return result;
          },
          {} as Record<string, string>
        );

        const initialBudgetPeriods = (item.periodBudgets as GLCodesBudgetByPeriodBudgets[]).reduce(
          (result, periodItem, index) => {
            const key = `period${(index + 1).toString().padStart(2, '0')} - Initial`;
            result[key] =
              periodItem.initialBudget !== null
                ? formatNumber(periodItem.initialBudget, 'c2')
                : 'N/A';
            return result;
          },
          {} as Record<string, string>
        );
        return {
          ...item,
          isActive: item.isActive ? 'Yes' : 'No',
          isFixed: item.isFixed ? 'Yes' : 'No',
          ...(isDepartmentIncluded ? [{ department: item?.department }] : []),
          ...forecastPeriods,
          ...initialBudgetPeriods,
        };
      });
      exportToExcel({
        columns,
        rows,
        fileName: `${buId ? `${buId}_` : ''}${
          sliceFiveChar(selectedCompany?.companyName || '') + '_'
        }${`FY${fiscalYear || ''}${selectedView === 0 ? `P${periods?.length || ''}_` : ''}`}${
          selectedYearDepartment?.departmentName !== 'All Departments'
            ? `${sliceFiveChar(selectedYearDepartment.departmentName || '')}_`
            : ''
        }${new Date().toLocaleString().replace(/[ ,]+/g, ' ')}`,
        columnMapping,
        sheetName: t(`FY${fiscalYear}`),
      });
    },
    [
      buId,
      dropDownYearValue,
      hasUsesInitialBudgetsPermission,
      selectedCompany?.companyName,
      selectedView,
      selectedYearDepartment.departmentName,
      t,
      viewCheckbookExcelColumns,
    ]
  );

  const onCheckbookDataStateChange = useCallback(
    (e: GridDataStateChangeEvent) => {
      const { dataState } = e;
      confirmBeforeProceeding(() => {
        setGlCodesBudgetByYear((years) => years.map((glCode) => ({ ...glCode, selected: false })));
        setYearCheckbookDataState(dataState);
      });
    },
    [confirmBeforeProceeding, setGlCodesBudgetByYear, setYearCheckbookDataState]
  );

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: any = event?.syntheticEvent?.target;
      const { checked } = checkboxElement;
      if (isEditMode) {
        setEditingGlCodeYearBudget((prev) => prev?.map((row) => ({ ...row, selected: checked })));
      } else {
        setGlCodesBudgetByYear((prev) => prev?.map((row) => ({ ...row, selected: checked })));
      }
    },
    [isEditMode, setEditingGlCodeYearBudget, setGlCodesBudgetByYear]
  );

  const onViewCheckbookSelection = useCallback(
    (event: GridSelectionChangeEvent) => {
      if (isEditMode) {
        setEditingGlCodeYearBudget((prev) => {
          const prevItems = [...prev].map((item) =>
            item.rowId === event?.dataItem.rowId ? { ...item, selected: !item.selected } : item
          );
          return prevItems;
        });
      } else {
        setGlCodesBudgetByYear((prev) => {
          const prevItems = [...prev].map((item) =>
            item.rowId === event?.dataItem.rowId ? { ...item, selected: !item.selected } : item
          );
          return prevItems;
        });
      }
    },
    [isEditMode, setEditingGlCodeYearBudget, setGlCodesBudgetByYear]
  );

  const handleItemChange = useCallback(
    (e: GridItemChangeEvent) => {
      const { field = '', value } = e;
      const { rowId } = e.dataItem || {};
      if (field.startsWith('initialBudgetPeriod')) {
        setEditingGlCodeYearBudget((prev) => [
          ...prev.map((item) => {
            if (rowId === item.rowId) {
              const numberSubstring = field.substring('initialBudgetPeriod'?.length);
              const index = toNumber(numberSubstring);

              if (!isNaN(index) && index >= 1) {
                if (item?.periodBudgets[index - 1]?.initialBudget !== undefined)
                  item.periodBudgets[index - 1].initialBudget = value;
              }
            }
            return {
              ...item,
              ...(rowId === item.rowId && {
                [field]: value,
              }),
            };
          }),
        ]);
      } else if (field.startsWith('budgetPeriod')) {
        setEditingGlCodeYearBudget((prev) => [
          ...prev.map((item) => {
            if (rowId === item.rowId) {
              const numberSubstring = field.substring('budgetPeriod'?.length);
              const index = toNumber(numberSubstring);

              if (!isNaN(index) && index >= 1) {
                if (item?.periodBudgets[index - 1]?.totalBudgetAmount !== undefined)
                  item.periodBudgets[index - 1].totalBudgetAmount = value;
              }
            }
            return {
              ...item,
              ...(rowId === item.rowId && {
                [field]: value,
              }),
            };
          }),
        ]);
      } else {
        setEditingGlCodeYearBudget((prev) => [
          ...prev.map((item) => ({
            ...item,
            ...(rowId === item.rowId && {
              [field]: value,
            }),
          })),
        ]);
      }
    },
    [setEditingGlCodeYearBudget]
  );

  const gridProps = useMemo(
    () =>
      width <= 1080
        ? {
            ...defaultProps,
            pageable: { ...defaultProps.pageable, buttonCount: 2 },
            sortable: isEditMode ? false : true,
          }
        : defaultProps,
    [isEditMode, width]
  );

  const { data: allPageData } = useMemo(
    () =>
      process(glCodesBudgetByYear, {
        filter: {
          logic: 'and',
          filters: getYearFilters(yearCheckbookDataState),
        },
        sort: getYearSorts(yearCheckbookDataState),
      }) as {
        data: any[];
        total: number;
      },
    [getYearFilters, getYearSorts, glCodesBudgetByYear, yearCheckbookDataState]
  );

  const gridToolbarChildren = useMemo(
    () => (
      <ViewCheckbookYearTableToolbar
        excelExport={excelExport}
        rows={yearViewData}
        setLoading={setExcelLoading}
        setSelectionFilter={setYearViewDropdownFilter}
        selectionFilter={yearViewDropdownFilter}
        setState={setYearCheckbookDataState}
        allPageData={allPageData}
      />
    ),
    [
      allPageData,
      excelExport,
      setYearCheckbookDataState,
      setYearViewDropdownFilter,
      yearViewData,
      yearViewDropdownFilter,
    ]
  );

  const handleDepartmentsClick = useCallback(
    ({ data }: { data: GLCodePeriodDepartment[] }) => {
      setDepartments(data);
      handleOpenDepartmentsModal();
    },
    [handleOpenDepartmentsModal]
  );

  return (
    <main>
      <ViewCheckbookActions />
      <YearFilterPills periods={dropDownYearValue?.periods?.length ?? 12} />
      <GridWrapper
        columns={yearViewColumns}
        id={'viewYearCheckbookTable'}
        dataSource={(isEditMode ? editingBudgetYearDataWithErrors : yearViewData) as any[]}
        total={yearViewTotal}
        dataState={yearCheckbookDataState}
        onDataStateChange={onCheckbookDataStateChange}
        height={`${gridHeight}px`}
        gridToolbarChildren={gridToolbarChildren}
        {...gridProps}
        showColumnMenu={true}
        fieldModifiable
        loading={isGlCodesBudgetByYearLoading || excelLoading}
        noRecordText="No records to display"
        onHeaderSelectionChange={onHeaderSelectionChange}
        onSelectionChange={onViewCheckbookSelection}
        onItemChange={handleItemChange}
        cellClick={handleDepartmentsClick}
        className="view-year-checkbook"
      />

      <Dialog
        width="400px"
        id="modal"
        title={t('Departments')}
        open={openDepartmentsModal}
        onClose={handleCloseDepartmentsModal}
      >
        <DepartmentsTable departments={departments} />
        <DialogActionsBar>
          <div className="flex justify-end">
            <Button onClick={handleCloseDepartmentsModal} color="info">
              {t('Close')}
            </Button>
          </div>
        </DialogActionsBar>
      </Dialog>
    </main>
  );
};

export default ViewYearCheckbookTable;
